import { useEffect, useRef, useState } from 'react'

import { useNavigation } from '@/hooks/useNavigation'
import { BiDoorOpen } from 'react-icons/bi'
import { HiArrowLeft, HiOutlineExclamation } from 'react-icons/hi'

import {
  Box,
  Circle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  SkeletonCircle,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'
import { useInViewRef } from 'rooks'

import { DrawerBreadcrumb, DrawerTabs, ShowVideoButton } from '@/components/ui'
import { DeviceFeed } from '@/features/device-feed'
import {
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { useDeviceQuery } from '@/graphql/generated/hooks'
import { AnalyticsScreen, useMixpanel } from '@/utils/analytics'
import { getDeviceStatusColor } from '@/utils/devices'

import { DeviceAlerts } from './DeviceAlerts'

interface IProps {
  defaultIndex: number
  deviceId: string
  isOpen: boolean
  onClose: () => void
}

export const ACDeviceDrawer = ({
  defaultIndex,
  isOpen,
  onClose,
  deviceId,
}: IProps) => {
  const { goToFloorPlan } = useNavigation()
  const { data, loading: isDeviceLoading } = useDeviceQuery({
    variables: {
      id: deviceId,
    },
    nextFetchPolicy: 'network-only',
  })
  const isLoading = !data || isDeviceLoading
  const [index, setIndex] = useState(defaultIndex)
  const [isPipActive, setPIPActive] = useState(false)
  const bodyRef = useRef<HTMLDivElement>()
  const [videoRef, isInView] = useInViewRef()
  const device = data?.device
  const incidents = device?.incidents?.edges?.map((i) => i?.node)

  const { createCameraEventHandler, createPlayerActionHandler } = useMixpanel(
    AnalyticsScreen.DeviceDrawer
  )

  const mixpanelData = () => {
    return {
      device_id: deviceId,
      facility_id: data?.device?.facility?.id,
      facility_address: data?.device?.facility?.address,
      floor_id: data?.device?.floor?.id,
      floor_name: data?.device?.floor?.name,
    }
  }
  const handleCameraEvent = createCameraEventHandler(mixpanelData())
  const handlePlayerAction = createPlayerActionHandler(mixpanelData())

  const {
    stream: { url: cameraFeedUrl, isLoading: isCameraFeedLoading },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    errorMessage,
  } = useFindDeviceFeed(data?.device?.id, handleCameraEvent)

  useEffect(() => {
    setIndex(defaultIndex)
  }, [defaultIndex])

  const disablePIP = () => setPIPActive(false)
  const enablePIP = () => setPIPActive(true)

  const scrollToVideoPlayer = () => {
    bodyRef.current.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const tabs = [
    // {
    //   label: 'All',
    //   icon: <BiListUl size={18} className='tab-btn__icon' />,
    //   panel: <DeviceEvents deviceId={deviceId} />,
    // },
    {
      label: 'Recent Incidents',
      icon: <HiOutlineExclamation className='tab-btn__icon' size={18} />,
      panel: <DeviceAlerts incidents={incidents} isLoading={isDeviceLoading} />,
    },
  ]
  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='lg'>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottom='1px solid #D5DCE4' p={0} pr='24px'>
          <Flex h='64px' w='full'>
            <HStack isTruncated pl='24px' spacing={2}>
              <IconButton
                aria-label='Back button'
                borderRadius='md'
                h='30px'
                icon={<HiArrowLeft size={18} />}
                minWidth='30px'
                onClick={onClose}
                variant='ghost'
                w='30px'
              />

              {isLoading ? (
                <SkeletonCircle size='30px' />
              ) : (
                <Circle
                  bg={getDeviceStatusColor(device?.status)}
                  color='#fff'
                  size='30px'
                >
                  <BiDoorOpen size={16} />
                </Circle>
              )}
              <HStack isTruncated spacing='14px'>
                <DrawerBreadcrumb
                  isLoading={isLoading}
                  label={device?.name}
                  maxWidth='175px'
                />
                <DrawerBreadcrumb
                  isLoading={isLoading}
                  label={`Floor ${device?.floor?.name}`}
                  maxWidth='175px'
                  onClick={() => {
                    onClose()
                    goToFloorPlan(device?.floor?.id)
                  }}
                />
                <DrawerBreadcrumb
                  hideDivider={true}
                  isLoading={isLoading}
                  label={device?.facility?.shortName}
                  maxWidth='175px'
                  onClick={() => {
                    onClose()
                    goToFloorPlan(device?.floor?.id)
                  }}
                />
              </HStack>
            </HStack>
          </Flex>
        </DrawerHeader>
        <DrawerBody p={0} ref={bodyRef}>
          <Box
            bg='white'
            position='sticky'
            ref={videoRef}
            top='-352px'
            zIndex='sticky'
          >
            <Box pt='24px' px='24px' w='100%'>
              <DeviceFeed
                isLoading={isCameraFeedLoading}
                onClickNext={nextCamera}
                onClickPrevious={previousCamera}
                showCameraSwitcher={availableCamerasIds.length > 1}
              >
                <VideoPlayer
                  disablePIP={disablePIP}
                  errorLabel={errorMessage}
                  onPlayerControlAction={handlePlayerAction}
                  onStartPlaying={() =>
                    handleCameraEvent(VideoFeedEvent.Playing)
                  }
                  pipActive={isPipActive}
                  showToggleLiveButton={false}
                  url={cameraFeedUrl}
                />
              </DeviceFeed>
            </Box>
          </Box>
          <Tabs index={index} isLazy onChange={(i) => setIndex(i)}>
            <Box
              bg='white'
              boxShadow='lg'
              position='sticky'
              top='0'
              zIndex='sticky'
            >
              <ShowVideoButton
                disablePIP={disablePIP}
                enablePIP={enablePIP}
                onClick={scrollToVideoPlayer}
                pipActive={isPipActive}
                show={!isInView}
                url={cameraFeedUrl}
              />
              <DrawerTabs tabs={tabs} />
              <Box
                bgGradient='linear-gradient(135deg, #2E6CDB 0%, #6096F5 100%)'
                h='4px'
                w='full'
              />
            </Box>

            <TabPanels>
              {tabs.map(({ panel, label }) => (
                <TabPanel key={label} p='0'>
                  {panel}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
